/**
 * V2TransitionManager - Transition manager for V2 StepNode screens
 *
 * Manages extraction, addition, removal, and update of transitions (edges)
 * from V2 StepNode.content using verified V2 field paths.
 *
 * V2 Edge Locations (10 confirmed + 2 defensive + 3 FlexibleLayout):
 * 1. content.actionBar.primary.action.edges      → actionKey: 'actionBar.primary'
 * 2. content.actionBar.secondary.action.edges     → actionKey: 'actionBar.secondary'
 * 3. content.header.skip.action.edges             → actionKey: 'header.skip'
 * 4. content.header.back.action.edges             → actionKey: 'header.back'
 * 5. content.options.items[i].action.edges        → actionKey: 'options.item.{i}'
 * 6. content.carousel.timer.action.edges          → actionKey: 'carousel.timer'
 * 7. content.stepper.timer.action.edges           → actionKey: 'stepper.timer'
 * 8. content.countdown.action.edges               → actionKey: 'countdown'
 * 9. content.systemPrompt.action.edges            → actionKey: 'systemPrompt'
 * 10. content.embed.callback.action.edges         → actionKey: 'embed.callback'
 *
 * Defensive (not in type definitions):
 * 11. content.actionBar.purchase.action.edges     → actionKey: 'actionBar.purchase'
 * 12. content.header.close.action.edges           → actionKey: 'header.close'
 *
 * V2 Paywall-specific (stored in content.paywall.*, NOT shared header/actionBar):
 * 13. content.paywall.closeButton.action.edges    → actionKey: 'paywall.closeButton'
 * 14. content.paywall.purchaseAction.action.edges → actionKey: 'paywall.purchaseAction'
 *
 * FlexibleLayout sections (dynamic):
 * 13. content.flexibleLayout.sections[i].buttonContent.action.edges             → 'section.{i}.button'
 * 14. content.flexibleLayout.sections[i].embedContent.callback.action.edges     → 'section.{i}.callback'
 * 15. content.flexibleLayout.sections[i].optionsListContent.items[j].action.edges → 'section.{i}.item.{j}'
 * 16. content.flexibleLayout.sections[i].paywallHeaderContent.closeButton.action.edges → 'section.{i}.close'
 *
 * V2 edge behavior:
 * - Edge conditions are stored and edited as native RouteGuard[]
 * - Paths are content-based and static for known StepNode variants
 * - Extraction is direct and does not route through the retired V1 EdgeExtractor
 *
 * Sprint: schema-v2-migration, Sprint 2
 * Related: OnboardingScreenTransitionManager (V1), TransitionIdGenerator, V2ScreenAdapter
 */

import type { StepNode, Trigger, Route, RouteGuard, ScreenTransition } from '@segmently/schema-models/v2';
import { TransitionIdGenerator } from '../../utils/converters/TransitionIdGenerator';
import { getV2ValidActionKeys } from '../shared/V2ActionHandleResolver';
import type { EntityTransition, TransitionRule } from '@modules/reactFlow/types/EntityTransition';
import {
  ensureV2SectionItemAction,
  findV2ActionableSectionItemCandidate,
  getV2ActionableSectionItems,
  getV2SectionActionCandidates,
} from '../shared/V2SectionActionDiscovery';

type V2Edge = Route;

/**
 * V2TransitionManager - Extracts, adds, removes, and updates transitions
 * in V2 StepNode content using V2 field paths.
 */
export class V2TransitionManager {

  // ============================================================================
  // READ OPERATIONS
  // ============================================================================

  /**
   * Extract all transitions from a V2 StepNode
   *
   * Walks all V2 edge locations, converts each V2 Edge to EntityTransition
   * using TransitionIdGenerator for deterministic IDs.
   *
   * @param step - V2 StepNode to extract transitions from
   * @returns Array of EntityTransition found in the step
   */
  extractTransitions(step: StepNode): EntityTransition[] {
    const stepId = (step as any).entityId || step.id;

    if (!stepId) {
      console.error('[V2TransitionManager] StepNode missing both entityId and id');
      throw new Error('StepNode missing both entityId and id - invalid flow state');
    }

    const transitions: EntityTransition[] = [];
    const c = step.content;
    const validActionKeys = getV2ValidActionKeys(step);

    if (!c) {
      return transitions;
    }

    const extractValid = (trigger: Trigger | undefined | null, actionKey: string): void => {
      if (!validActionKeys.has(actionKey)) {
        return;
      }
      this.extractFromTrigger(stepId, trigger, actionKey, transitions);
    };

    // ActionBar buttons
    extractValid(c.actionBar?.primary?.action, 'actionBar.primary');
    extractValid(c.actionBar?.secondary?.action, 'actionBar.secondary');
    // Defensive: purchase
    extractValid((c.actionBar as any)?.purchase?.action, 'actionBar.purchase');

    // Header buttons
    extractValid(c.header?.skip?.action, 'header.skip');
    extractValid(c.header?.back?.action, 'header.back');
    // Defensive: close
    extractValid((c.header as any)?.close?.action, 'header.close');

    // V2 Paywall-specific buttons (stored in content.paywall.*, NOT in shared header/actionBar)
    extractValid((c as any).paywall?.closeButton?.action, 'paywall.closeButton');
    extractValid((c as any).paywall?.purchaseAction?.action, 'paywall.purchaseAction');
    // Downsale: onCancelAction is a standard Trigger on the purchase button
    extractValid((c as any).paywall?.purchaseAction?.action?.onCancelAction, 'paywall.purchaseCancel');

    // Options items (dynamic)
    if (c.options?.items) {
      c.options.items.forEach((item, i) => {
        extractValid(item.action, `options.item.${i}`);
      });
    }

    // Countdown timers
    extractValid(c.carousel?.timer?.action, 'carousel.timer');
    extractValid(c.stepper?.timer?.action, 'stepper.timer');
    extractValid(c.countdown?.action, 'countdown');

    // SystemPrompt
    extractValid(c.systemPrompt?.action, 'systemPrompt');

    // Embed callback
    extractValid(c.embed?.callback?.action, 'embed.callback');

    // FlexibleLayout sections
    if (c.flexibleLayout?.sections) {
      (c.flexibleLayout.sections as any[]).forEach((section: any) => {
        if (!section.id) return;
        const sectionId = section.id as string;
        // Button section
        extractValid(section.buttonContent?.action, `section.${sectionId}.button`);
        // Downsale: onCancelAction on button section's Purchase trigger
        extractValid(section.buttonContent?.action?.onCancelAction, `section.${sectionId}.purchaseCancel`);
        // CustomEmbed callback
        extractValid(section.embedContent?.callback?.action, `section.${sectionId}.callback`);
        // OptionsList per-item actions
        getV2ActionableSectionItems(section).forEach((item: any, j: number) => {
          extractValid(item.action, `section.${sectionId}.item.${j}`);
        });
        // PaywallHeader close button
        extractValid(section.paywallHeaderContent?.closeButton?.action, `section.${sectionId}.close`);
      });
    }

    // WebEmbed child data sources and nested CustomEmbed child data sources
    getV2SectionActionCandidates(c).forEach((candidate) => {
      if (candidate.scope === 'flexible-section' || !candidate.section?.id) return;
      const sectionId = candidate.section.id as string;
      extractValid(candidate.section.buttonContent?.action, `section.${sectionId}.button`);
      getV2ActionableSectionItems(candidate.section).forEach((item: any, j: number) => {
        extractValid(item.action, `section.${sectionId}.item.${j}`);
      });
    });

    // StickyContainer sections
    if (c.flexibleLayout?.stickyContainer?.sections) {
      (c.flexibleLayout.stickyContainer.sections as any[]).forEach((section: any) => {
        if (!section.id) return;
        const sectionId = section.id as string;
        // Button
        extractValid(section.buttonContent?.action, `sticky.${sectionId}.button`);
        // Downsale
        extractValid(section.buttonContent?.action?.onCancelAction, `sticky.${sectionId}.purchaseCancel`);
      });
    }

    // Debug log for edges with conditions
    const edgesWithRules = transitions.filter(t => t.rules && t.rules.length > 0);
    if (edgesWithRules.length > 0) {
      console.log(`[V2TransitionManager] Step ${stepId.slice(0, 12)} has edges with conditions:`, edgesWithRules.map(t => ({
        actionKey: t.sourceActionKey,
        target: t.targetEntityId?.slice(0, 12),
        rulesCount: t.rules?.length,
      })));
    }

    return transitions;
  }

  /**
   * Extract transitions from a single Trigger
   */
  private extractFromTrigger(
    stepId: string,
    trigger: Trigger | undefined | null,
    actionKey: string,
    out: EntityTransition[]
  ): void {
    if (!trigger?.edges || !Array.isArray(trigger.edges)) {
      return;
    }

    for (const edge of trigger.edges) {
      if (!edge.nextScreenId) {
        continue;
      }

      out.push({
        id: TransitionIdGenerator.generate(stepId, actionKey, edge.nextScreenId),
        sourceEntityId: stepId,
        targetEntityId: edge.nextScreenId,
        sourceActionKey: actionKey,
        label: undefined,
        rules: (edge.conditions || []).map(routeGuardToTransitionRule),
        transition: edge.transition,
      });
    }
  }

  // ============================================================================
  // WRITE OPERATIONS
  // ============================================================================

  /**
   * Add a transition to a V2 StepNode
   *
   * Creates the necessary Trigger structure if it doesn't exist.
   * Returns a new StepNode (immutable update).
   *
   * @param step - Original StepNode
   * @param transition - Transition to add
   * @returns Updated StepNode with the new transition
   */
  addTransition(step: StepNode, transition: EntityTransition): StepNode {
    const newStep: StepNode = JSON.parse(JSON.stringify(step));
    const c = newStep.content;

    if (!c) {
      console.warn('[V2TransitionManager] Cannot add transition: step has no content');
      return step;
    }

    const v2Edge: V2Edge = {
      nextScreenId: transition.targetEntityId,
      transitionKind: 'push',
      conditions: (transition.rules || []).map(transitionRuleToRouteGuard),
    };

    const actionKey = transition.sourceActionKey;
    const actionKind = this.getDefaultActionKind(actionKey);

    // Route to appropriate content path and ensure structure exists
    if (actionKey === 'actionBar.primary') {
      this.ensureTapTargetAction(c, ['actionBar', 'primary'], actionKind);
      c.actionBar!.primary!.action.edges.push(v2Edge);
    } else if (actionKey === 'actionBar.secondary') {
      this.ensureTapTargetAction(c, ['actionBar', 'secondary'], actionKind);
      c.actionBar!.secondary!.action.edges.push(v2Edge);
    } else if (actionKey === 'actionBar.purchase') {
      this.ensureTapTargetAction(c, ['actionBar', 'purchase'], 'advance');
      (c.actionBar as any).purchase.action.edges.push(v2Edge);
    } else if (actionKey === 'header.skip') {
      this.ensureTapTargetAction(c, ['header', 'skip'], actionKind);
      c.header!.skip!.action.edges.push(v2Edge);
    } else if (actionKey === 'header.back') {
      this.ensureTapTargetAction(c, ['header', 'back'], 'retreat');
      c.header!.back!.action.edges.push(v2Edge);
    } else if (actionKey === 'header.close') {
      this.ensureTapTargetAction(c, ['header', 'close'], actionKind);
      (c.header as any).close.action.edges.push(v2Edge);
    } else if (actionKey.startsWith('options.item.')) {
      const itemIndex = parseInt(actionKey.split('.')[2], 10);
      this.ensureOptionsItemAction(c, itemIndex, actionKind);
      c.options!.items[itemIndex].action!.edges.push(v2Edge);
    } else if (actionKey === 'carousel.timer') {
      this.ensureCountdownAction(c, ['carousel', 'timer'], actionKind);
      c.carousel!.timer.action.edges.push(v2Edge);
    } else if (actionKey === 'stepper.timer') {
      this.ensureCountdownAction(c, ['stepper', 'timer'], actionKind);
      c.stepper!.timer.action.edges.push(v2Edge);
    } else if (actionKey === 'countdown') {
      this.ensureRootCountdownAction(c, actionKind);
      c.countdown!.action.edges.push(v2Edge);
    } else if (actionKey === 'systemPrompt') {
      this.ensureSystemPromptAction(c, actionKind);
      c.systemPrompt!.action.edges.push(v2Edge);
    } else if (actionKey === 'embed.callback') {
      this.ensureEmbedCallbackAction(c, actionKind);
      c.embed!.callback.action.edges.push(v2Edge);
    } else if (actionKey === 'paywall.closeButton') {
      this.ensureTapTargetAction(c, ['paywall', 'closeButton'], actionKind);
      (c as any).paywall.closeButton.action.edges.push(v2Edge);
    } else if (actionKey === 'paywall.purchaseAction') {
      this.ensureTapTargetAction(c, ['paywall', 'purchaseAction'], 'purchase');
      (c as any).paywall.purchaseAction.action.edges.push(v2Edge);
    } else if (actionKey === 'paywall.purchaseCancel') {
      // Downsale: ensure onCancelAction Trigger exists on purchase button
      this.ensureTapTargetAction(c, ['paywall', 'purchaseAction'], 'purchase');
      if (!(c as any).paywall.purchaseAction.action.onCancelAction) {
        (c as any).paywall.purchaseAction.action.onCancelAction = { kind: 'advance', edges: [] };
      }
      (c as any).paywall.purchaseAction.action.onCancelAction.edges.push(v2Edge);
    } else if (actionKey.startsWith('section.')) {
      this.ensureFlexibleSectionAction(c, actionKey, actionKind);
      const edges = this.getEdgesForActionKey(newStep, actionKey);
      if (edges) {
        edges.push(v2Edge);
      } else {
        console.warn(`[V2TransitionManager] Could not create section structure for: ${actionKey}`);
        return step;
      }
    } else if (actionKey.startsWith('sticky.')) {
      this.ensureStickyContainerAction(c, actionKey, actionKind);
      const edges = this.getEdgesForActionKey(newStep, actionKey);
      if (edges) {
        edges.push(v2Edge);
      } else {
        console.warn(`[V2TransitionManager] Could not create sticky container structure for: ${actionKey}`);
        return step;
      }
    } else {
      console.warn(`[V2TransitionManager] Unknown action key: ${actionKey}`);
      return step;
    }

    return newStep;
  }

  /**
   * Remove a transition from a V2 StepNode
   *
   * @param step - Original StepNode
   * @param transitionId - ID of transition to remove
   * @returns Updated StepNode without the transition
   */
  removeTransition(step: StepNode, transitionId: string): StepNode {
    const newStep: StepNode = JSON.parse(JSON.stringify(step));

    let parsed;
    try {
      parsed = TransitionIdGenerator.parse(transitionId);
    } catch {
      console.warn(`[V2TransitionManager] Invalid transition ID: ${transitionId}`);
      return step;
    }

    const { sourceActionKey, targetEntityId } = parsed;
    const edges = this.getEdgesForActionKey(newStep, sourceActionKey);

    if (!edges) {
      console.warn(`[V2TransitionManager] No edges for action ${sourceActionKey}`);
      return step;
    }

    const edgeIndex = edges.findIndex((e: V2Edge) => e.nextScreenId === targetEntityId);
    if (edgeIndex !== -1) {
      edges.splice(edgeIndex, 1);
    }

    return newStep;
  }

  /**
   * Update a transition in a V2 StepNode
   *
   * @param step - Original StepNode
   * @param transitionId - ID of transition to update
   * @param updates - Partial transition data to update
   * @returns Updated StepNode with modified transition
   */
  updateTransition(
    step: StepNode,
    transitionId: string,
    updates: Partial<EntityTransition>
  ): StepNode {
    const newStep: StepNode = JSON.parse(JSON.stringify(step));

    let parsed;
    try {
      parsed = TransitionIdGenerator.parse(transitionId);
    } catch {
      console.warn(`[V2TransitionManager] Invalid transition ID: ${transitionId}`);
      return step;
    }

    const { sourceActionKey, targetEntityId: oldTargetId } = parsed;
    const edges = this.getEdgesForActionKey(newStep, sourceActionKey);

    if (!edges) {
      console.warn(`[V2TransitionManager] No edges for action ${sourceActionKey}`);
      return step;
    }

    const edgeIndex = edges.findIndex((e: V2Edge) => e.nextScreenId === oldTargetId);
    if (edgeIndex === -1) {
      console.warn(`[V2TransitionManager] Edge not found: ${transitionId}`);
      return step;
    }

    if (updates.targetEntityId !== undefined) {
      edges[edgeIndex].nextScreenId = updates.targetEntityId;
    }

    return newStep;
  }

  /**
   * Read RouteGuard[] directly from an edge (V2 native — no V1 conversion).
   * Preserves `logic` fields for AND/OR group computation.
   *
   * @param step - V2 StepNode to read from
   * @param transitionId - Transition ID (sourceId|actionKey|targetId)
   * @returns RouteGuard[] with logic connectors, or [] if not found
   */
  getTransitionRouteGuards(step: StepNode, transitionId: string): RouteGuard[] {
    let parsed;
    try {
      parsed = TransitionIdGenerator.parse(transitionId);
    } catch {
      console.warn(`[V2TransitionManager] Invalid transition ID: ${transitionId}`);
      return [];
    }

    const { sourceActionKey, targetEntityId } = parsed;
    const edges = this.getEdgesForActionKey(step, sourceActionKey);

    if (!edges) return [];

    const edge = edges.find((e: V2Edge) => e.nextScreenId === targetEntityId);
    return edge?.conditions ? [...edge.conditions] : [];
  }

  /**
   * Write RouteGuard[] directly to an edge (V2 native — preserves logic fields).
   * Use this instead of updateTransitionRules() for V2 canvas condition editing.
   *
   * @param step - Original StepNode
   * @param transitionId - ID of transition to update
   * @param conditions - New RouteGuard[] with logic connectors
   * @returns Updated StepNode with modified conditions
   */
  updateTransitionRouteGuards(
    step: StepNode,
    transitionId: string,
    conditions: RouteGuard[],
  ): StepNode {
    const newStep: StepNode = JSON.parse(JSON.stringify(step));

    let parsed;
    try {
      parsed = TransitionIdGenerator.parse(transitionId);
    } catch {
      console.warn(`[V2TransitionManager] Invalid transition ID: ${transitionId}`);
      return step;
    }

    const { sourceActionKey, targetEntityId } = parsed;
    const edges = this.getEdgesForActionKey(newStep, sourceActionKey);

    if (!edges) {
      console.warn(`[V2TransitionManager] No edges for action ${sourceActionKey}`);
      return step;
    }

    const edgeIndex = edges.findIndex((e: V2Edge) => e.nextScreenId === targetEntityId);
    if (edgeIndex === -1) {
      console.warn(`[V2TransitionManager] Edge not found: ${transitionId}`);
      return step;
    }

    edges[edgeIndex].conditions = conditions;

    return newStep;
  }

  /**
   * Read the per-edge visual transition override (Route.transition, schema
   * 2.17) — V2 native, mirrors getTransitionRouteGuards.
   *
   * @param step - V2 StepNode to read from
   * @param transitionId - Transition ID (sourceId|actionKey|targetId)
   * @returns The ScreenTransition override, or null when the edge inherits
   */
  getTransitionScreenTransition(step: StepNode, transitionId: string): ScreenTransition | null {
    let parsed;
    try {
      parsed = TransitionIdGenerator.parse(transitionId);
    } catch {
      console.warn(`[V2TransitionManager] Invalid transition ID: ${transitionId}`);
      return null;
    }

    const { sourceActionKey, targetEntityId } = parsed;
    const edges = this.getEdgesForActionKey(step, sourceActionKey);
    if (!edges) return null;

    const edge = edges.find((e: V2Edge) => e.nextScreenId === targetEntityId);
    return edge?.transition ?? null;
  }

  /**
   * Write (or clear) the per-edge visual transition override — mirrors
   * updateTransitionRouteGuards; covers all 16 trigger action-key families
   * via getEdgesForActionKey.
   *
   * @param step - Original StepNode
   * @param transitionId - ID of transition to update
   * @param transition - Override to set; null DELETES the key ("Inherit" —
   *   unset ≠ none: the edge falls back to perKind/default resolution)
   * @returns Updated StepNode with the modified edge
   */
  updateTransitionScreenTransition(
    step: StepNode,
    transitionId: string,
    transition: ScreenTransition | null,
  ): StepNode {
    const newStep: StepNode = JSON.parse(JSON.stringify(step));

    let parsed;
    try {
      parsed = TransitionIdGenerator.parse(transitionId);
    } catch {
      console.warn(`[V2TransitionManager] Invalid transition ID: ${transitionId}`);
      return step;
    }

    const { sourceActionKey, targetEntityId } = parsed;
    const edges = this.getEdgesForActionKey(newStep, sourceActionKey);
    if (!edges) {
      console.warn(`[V2TransitionManager] No edges for action ${sourceActionKey}`);
      return step;
    }

    const edgeIndex = edges.findIndex((e: V2Edge) => e.nextScreenId === targetEntityId);
    if (edgeIndex === -1) {
      console.warn(`[V2TransitionManager] Edge not found: ${transitionId}`);
      return step;
    }

    if (transition === null) {
      delete edges[edgeIndex].transition;
    } else {
      edges[edgeIndex].transition = transition;
    }

    return newStep;
  }

  /**
   * Update the route guards for a specific transition.
   *
   * @param step - Original StepNode
   * @param transitionId - ID of transition to update
   * @param rules - New V2 route guards to set
   * @returns Updated StepNode with modified transition conditions
   */
  updateTransitionRules(
    step: StepNode,
    transitionId: string,
    rules: RouteGuard[]
  ): StepNode {
    const newStep: StepNode = JSON.parse(JSON.stringify(step));

    let parsed;
    try {
      parsed = TransitionIdGenerator.parse(transitionId);
    } catch {
      console.warn(`[V2TransitionManager] Invalid transition ID: ${transitionId}`);
      return step;
    }

    const { sourceActionKey, targetEntityId } = parsed;
    const edges = this.getEdgesForActionKey(newStep, sourceActionKey);

    if (!edges) {
      console.warn(`[V2TransitionManager] No edges for action ${sourceActionKey}`);
      return step;
    }

    const edgeIndex = edges.findIndex((e: V2Edge) => e.nextScreenId === targetEntityId);
    if (edgeIndex === -1) {
      console.warn(`[V2TransitionManager] Edge not found: ${transitionId}`);
      return step;
    }

    edges[edgeIndex].conditions = rules;

    return newStep;
  }

  // ============================================================================
  // EDGE ACCESS HELPERS
  // ============================================================================

  /**
   * Get the edges array for a given V2 action key
   *
   * Maps action key string → content path and returns the edges array reference.
   * Returns undefined if the path doesn't exist.
   */
  private getEdgesForActionKey(step: StepNode, actionKey: string): V2Edge[] | undefined {
    const c = step.content;
    if (!c) return undefined;

    if (actionKey === 'actionBar.primary') {
      return c.actionBar?.primary?.action?.edges;
    }
    if (actionKey === 'actionBar.secondary') {
      return c.actionBar?.secondary?.action?.edges;
    }
    if (actionKey === 'actionBar.purchase') {
      return (c.actionBar as any)?.purchase?.action?.edges;
    }
    if (actionKey === 'header.skip') {
      return c.header?.skip?.action?.edges;
    }
    if (actionKey === 'header.back') {
      return c.header?.back?.action?.edges;
    }
    if (actionKey === 'header.close') {
      return (c.header as any)?.close?.action?.edges;
    }
    if (actionKey.startsWith('options.item.')) {
      const itemIndex = parseInt(actionKey.split('.')[2], 10);
      return c.options?.items?.[itemIndex]?.action?.edges;
    }
    if (actionKey === 'carousel.timer') {
      return c.carousel?.timer?.action?.edges;
    }
    if (actionKey === 'stepper.timer') {
      return c.stepper?.timer?.action?.edges;
    }
    if (actionKey === 'countdown') {
      return c.countdown?.action?.edges;
    }
    if (actionKey === 'systemPrompt') {
      return c.systemPrompt?.action?.edges;
    }
    if (actionKey === 'embed.callback') {
      return c.embed?.callback?.action?.edges;
    }

    // V2 Paywall-specific buttons
    if (actionKey === 'paywall.closeButton') {
      return (c as any).paywall?.closeButton?.action?.edges;
    }
    if (actionKey === 'paywall.purchaseAction') {
      return (c as any).paywall?.purchaseAction?.action?.edges;
    }
    // Downsale: onCancelAction is a standard Trigger with edges[]
    if (actionKey === 'paywall.purchaseCancel') {
      return (c as any).paywall?.purchaseAction?.action?.onCancelAction?.edges;
    }

    // Section keys: section.{sectionId}.button | section.{sectionId}.callback | section.{sectionId}.item.{j}
    if (actionKey.startsWith('section.')) {
      const parts = actionKey.split('.');
      const sectionId = parts[1];
      if (parts[2] === 'item') {
        const itemIndex = parseInt(parts[3], 10);
        const candidate = findV2ActionableSectionItemCandidate(c, sectionId);
        return candidate?.section?.optionsListContent?.items?.[itemIndex]?.action?.edges;
      }

      const section = getV2SectionActionCandidates(c).find((candidate) => (
        candidate.section?.id === sectionId
      ))?.section;
      if (!section) return undefined;

      if (parts[2] === 'button') {
        return section.buttonContent?.action?.edges;
      }
      if (parts[2] === 'callback') {
        return section.embedContent?.callback?.action?.edges;
      }
      if (parts[2] === 'close') {
        return section.paywallHeaderContent?.closeButton?.action?.edges;
      }
      // Downsale: onCancelAction on button section's Purchase trigger
      if (parts[2] === 'purchaseCancel') {
        return section.buttonContent?.action?.onCancelAction?.edges;
      }
    }

    // StickyContainer section keys: sticky.{sectionId}.button | sticky.{sectionId}.purchaseCancel
    if (actionKey.startsWith('sticky.')) {
      const parts = actionKey.split('.');
      const sectionId = parts[1];
      const stickySections = (c as any).flexibleLayout?.stickyContainer?.sections as any[] | undefined;
      const section = stickySections?.find((s: any) => s.id === sectionId);
      if (!section) return undefined;

      if (parts[2] === 'button') {
        return section.buttonContent?.action?.edges;
      }
      if (parts[2] === 'purchaseCancel') {
        return section.buttonContent?.action?.onCancelAction?.edges;
      }
    }

    return undefined;
  }

  // ============================================================================
  // STRUCTURE BUILDERS (ensure Trigger exists at path)
  // ============================================================================

  /**
   * Get default ActionKind based on action location
   */
  private getDefaultActionKind(actionKey: string): string {
    if (actionKey === 'header.back') {
      return 'retreat';
    }
    return 'advance';
  }

  /**
   * Ensure TapTarget action (Trigger) exists at a content path
   * For: actionBar.primary/secondary/purchase, header.skip/back/close
   */
  private ensureTapTargetAction(content: any, path: string[], kind: string): void {
    let current = content;
    for (let i = 0; i < path.length; i++) {
      const key = path[i];
      if (!current[key]) {
        current[key] = {};
      }
      current = current[key];
    }
    // current is now the TapTarget
    if (!current.action) {
      current.action = { kind, edges: [] };
    }
    if (!current.action.edges) {
      current.action.edges = [];
    }
    if (!current.action.kind) {
      current.action.kind = kind;
    }
  }

  /**
   * Ensure OptionsItem action exists at index
   */
  private ensureOptionsItemAction(content: any, itemIndex: number, kind: string): void {
    if (!content.options) {
      content.options = { items: [] };
    }
    if (!content.options.items) {
      content.options.items = [];
    }
    while (content.options.items.length <= itemIndex) {
      content.options.items.push({ title: { translations: {} } });
    }
    const item = content.options.items[itemIndex];
    if (!item.action) {
      item.action = { kind, edges: [] };
    }
    if (!item.action.edges) {
      item.action.edges = [];
    }
    if (!item.action.kind) {
      item.action.kind = kind;
    }
  }

  /**
   * Ensure Countdown.action exists at path
   * For: carousel.timer, stepper.timer
   */
  private ensureCountdownAction(content: any, path: string[], kind: string): void {
    let current = content;
    for (const key of path) {
      if (!current[key]) {
        current[key] = {};
      }
      current = current[key];
    }
    // current is now the Countdown
    if (!current.action) {
      current.action = { kind, edges: [] };
    }
    if (!current.action.edges) {
      current.action.edges = [];
    }
  }

  /**
   * Ensure root-level countdown action exists
   */
  private ensureRootCountdownAction(content: any, kind: string): void {
    if (!content.countdown) {
      content.countdown = { duration: 0, action: { kind, edges: [] } };
    }
    if (!content.countdown.action) {
      content.countdown.action = { kind, edges: [] };
    }
    if (!content.countdown.action.edges) {
      content.countdown.action.edges = [];
    }
  }

  /**
   * Ensure SystemPrompt action exists
   */
  private ensureSystemPromptAction(content: any, kind: string): void {
    if (!content.systemPrompt) {
      content.systemPrompt = { permissionType: 'Notification', value: false, action: { kind, edges: [] } };
    }
    if (!content.systemPrompt.action) {
      content.systemPrompt.action = { kind, edges: [] };
    }
    if (!content.systemPrompt.action.edges) {
      content.systemPrompt.action.edges = [];
    }
  }

  /**
   * Ensure EmbedConfig.callback action exists
   */
  private ensureEmbedCallbackAction(content: any, kind: string): void {
    if (!content.embed) {
      content.embed = { callback: { action: { kind, edges: [] } }, inputs: {}, outputs: {} };
    }
    if (!content.embed.callback) {
      content.embed.callback = { action: { kind, edges: [] } };
    }
    if (!content.embed.callback.action) {
      content.embed.callback.action = { kind, edges: [] };
    }
    if (!content.embed.callback.action.edges) {
      content.embed.callback.action.edges = [];
    }
  }

  /**
   * Ensure FlexibleLayout section action structure exists.
   * Handles: section.{sectionId}.button, section.{sectionId}.callback, section.{sectionId}.item.{j}
   */
  private ensureFlexibleSectionAction(content: any, actionKey: string, kind: string): void {
    const parts = actionKey.split('.');
    const sectionId = parts[1];

    if (parts[2] === 'item') {
      ensureV2SectionItemAction(content, sectionId, parseInt(parts[3], 10), kind);
      return;
    }

    // Find section by ID across top-level FlexibleLayout sections, WebEmbed data
    // sources, and nested CustomEmbed data sources (can't auto-create by UUID).
    const section = getV2SectionActionCandidates(content).find((candidate) => (
      candidate.section?.id === sectionId
    ))?.section;
    if (!section) return;

    if (parts[2] === 'button') {
      if (!section.buttonContent) {
        section.buttonContent = { kind: 'label', content: { translations: {} }, appearance: {}, action: { kind, edges: [] } };
      }
      if (!section.buttonContent.action) {
        section.buttonContent.action = { kind, edges: [] };
      }
      if (!section.buttonContent.action.edges) {
        section.buttonContent.action.edges = [];
      }
    } else if (parts[2] === 'callback') {
      if (!section.embedContent) {
        section.embedContent = { callback: { action: { kind, edges: [] } } };
      }
      if (!section.embedContent.callback) {
        section.embedContent.callback = { action: { kind, edges: [] } };
      }
      if (!section.embedContent.callback.action) {
        section.embedContent.callback.action = { kind, edges: [] };
      }
      if (!section.embedContent.callback.action.edges) {
        section.embedContent.callback.action.edges = [];
      }
    } else if (parts[2] === 'close') {
      if (!section.paywallHeaderContent) {
        section.paywallHeaderContent = { closeButton: { action: { kind, edges: [] } } };
      }
      if (!section.paywallHeaderContent.closeButton) {
        section.paywallHeaderContent.closeButton = { action: { kind, edges: [] } };
      }
      if (!section.paywallHeaderContent.closeButton.action) {
        section.paywallHeaderContent.closeButton.action = { kind, edges: [] };
      }
      if (!section.paywallHeaderContent.closeButton.action.edges) {
        section.paywallHeaderContent.closeButton.action.edges = [];
      }
    } else if (parts[2] === 'purchaseCancel') {
      // Downsale: ensure onCancelAction Trigger exists on button section
      if (!section.buttonContent?.action) return;
      if (!section.buttonContent.action.onCancelAction) {
        section.buttonContent.action.onCancelAction = { kind: 'advance', edges: [] };
      }
      if (!section.buttonContent.action.onCancelAction.edges) {
        section.buttonContent.action.onCancelAction.edges = [];
      }
    }
  }

  private ensureStickyContainerAction(content: any, actionKey: string, kind: string): void {
    if (!content.flexibleLayout?.stickyContainer?.sections) return;

    const parts = actionKey.split('.');
    const sectionId = parts[1];

    const section = (content.flexibleLayout.stickyContainer.sections as any[])
      .find((s: any) => s.id === sectionId);
    if (!section) return;

    if (parts[2] === 'button') {
      if (!section.buttonContent) {
        section.buttonContent = { kind: 'label', content: { translations: {} }, appearance: {}, action: { kind, edges: [] } };
      }
      if (!section.buttonContent.action) {
        section.buttonContent.action = { kind, edges: [] };
      }
      if (!section.buttonContent.action.edges) {
        section.buttonContent.action.edges = [];
      }
    } else if (parts[2] === 'purchaseCancel') {
      if (!section.buttonContent?.action) return;
      if (!section.buttonContent.action.onCancelAction) {
        section.buttonContent.action.onCancelAction = { kind: 'advance', edges: [] };
      }
      if (!section.buttonContent.action.onCancelAction.edges) {
        section.buttonContent.action.onCancelAction.edges = [];
      }
    }
  }
}

function routeGuardToTransitionRule(guard: RouteGuard): TransitionRule {
  return {
    key: guard.key,
    operator: guard.operator,
    value: guard.value,
    logic: guard.logic === 'and' ? 'AND' : guard.logic === 'or' ? 'OR' : undefined,
  };
}

function transitionRuleToRouteGuard(rule: TransitionRule): RouteGuard {
  // Case-insensitive: the editor writes 'AND'/'OR', CLI custom-screen paths write the
  // schema's lowercase; RouteGuard.logic (schema ConditionLogic) is lowercase.
  const logic = typeof rule.logic === 'string' ? rule.logic.toLowerCase() : undefined;
  return {
    key: rule.key,
    operator: rule.operator as RouteGuard['operator'],
    value: rule.value,
    logic: logic === 'and' || logic === 'or' ? logic : undefined,
  };
}

// Singleton instance
export const v2TransitionManager = new V2TransitionManager();
